import React, { useMemo, useEffect } from 'react';

/* ================== CONSTANTE ================== */

const GENERAL_AMENITIES = [
  'WiFi gratuit', 'Parcare privata', 'Restaurant', 'Piscina incalzita', 'Sala de sport', 'Piscina interioara', 'Piscina exterioara',
  'Ciubar', 'Jacuzzi', 'Sauna uscata', 'Sauna umeda', 'Sala de fitness', 'Masaj', 'Tratamente Spa', 'Sala de jocuri', 'Gratar',
  'Baie proprie', 'Baie comuna', 'Bucatarie', 'Crama sau pivnita', 'Restaurant cu specific', 'Bar', 'Parc de distractii',
  'Loc pentru animale de companie', 'Spatiu de joaca pentru copii', 'Inchirieri biciclete', 'Inchirieri echipament sportiv',
  'Plimbari de agrement', 'Sala de conferinte', 'Sala de evenimente', 'Inchirieri auto', 'TV in camera', 'Cada cu hidromasaj',
  'Semineu', 'Soba pe lemne', 'Plata cu card bancar', 'Plata cu vouchere de vacanta'
];

const AGROTURISM_AMENITIES = [
  'Animale de ferma', 'Plante medicinale', 'Turism ecologic', 'Bucatarie traditionala', 'Produse de ferma', 'Degustari produse proprii',
  'Culesul granelor', 'Tur ghidat al fermei', 'Hranirea animalelor', 'Pasune / Stana', 'Zona cu animale prietenoase',
  'Atelier de olarit', 'Atelier de preparare a branzeturilor', 'Atelier de coacere a painii',
  'Atelier de preparare dulceturi', 'Atelier de preparare a sucurilor naturale', 'Atelier de tesut',
  'Atelier de prelucrare a lemnului', 'Ateliere specifice pentru copii', 'Gradina cu legume',
  'Gradina cu zarzavaturi', 'Plimbari cu caruta', 'Calarie', 'Camere in stil traditional',
  'Camere cu vatra sau semineu', 'Cuptor traditional', 'Izolatie naturala', 'Pescuit', 'Munca la pamant'
];

const ROOM_TYPES = ['Single', 'Dubla', 'Twin', 'Tripla', 'Apartament'];

const CHILD_RULES = [
  { min: 0, max: 2, childDiscount: 100, mealPrice: 0, note: 'Patut / pat existent' },
  { min: 3, max: 5, childDiscount: 50, mealPrice: 10, note: 'Poate depinde de pachet masa' },
  { min: 6, max: 12, childDiscount: 50, mealPrice: 20, note: 'Pat suplimentar' },
  { min: 13, max: 99, childDiscount: 0, mealPrice: 40, note: 'Considerat adult' }
];

const getChildRule = (age) =>
  CHILD_RULES.find(r => age >= r.min && age <= r.max) || CHILD_RULES[3];

/* ================== COUNTER ================== */

const Counter = ({ value, min = 0, max = Infinity, step = 1, suffix, onChange }) => (
  <div style={counterWrap}>
    <button onClick={() => value > min && onChange(value - step)} style={counterBtn}>−</button>
    <span style={{ minWidth: 22, textAlign: 'center' }}>{value}{suffix || ''}</span>
    <button onClick={() => value < max && onChange(value + step)} style={counterBtn}>+</button>
  </div>
);

const counterWrap = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 6px',
  border: '1px solid #006d77',
  borderRadius: 14,
  fontSize: 12,
  fontFamily: 'Sora, sans-serif'
};

const counterBtn = {
  border: 'none',
  background: 'transparent',
  color: '#006d77',
  fontWeight: 700,
  cursor: 'pointer'
};

/* ================== COMPONENTA ================== */

export default function PropertyAmenities({
  property,
  amenities = {},
  editMode,
  isHost,
  guestMode,
  form,
  onChange,
  propertyId,
  setForm
}) {
  const canEditAmenities = isHost && editMode;

  /* ===== fetch date proprietate pentru guest ===== */
  useEffect(() => {
    if (guestMode && propertyId && form.checkIn && form.checkOut) {
      // 1️⃣ Fetch detalii proprietate
      fetch(`/api/property/${propertyId}`)
        .then(r => r.json())
        .then(d => {
          console.log('Fetched property details', d);
          onChange({ target: { name: 'roomsPrice', value: d.roomsPrice || {} } });
          onChange({ target: { name: 'mealOptions', value: d.mealOptions || {} } });
          onChange({ target: { name: 'selectedMeal', value: d.selectedMeal || '' } });
          onChange({ target: { name: 'childRules', value: d.childRules || CHILD_RULES } });
        });

      // 2️⃣ Fetch disponibilitate camere
      fetch(
        `/api/property/${propertyId}/availability?checkIn=${form.checkIn}&checkOut=${form.checkOut}`
      )
        .then(r => r.json())
        .then(d => {
          console.log('Rooms available', d.roomsAvailable);
          onChange({ target: { name: 'roomsAvailable', value: d.roomsAvailable } });
        });
    }
  }, [guestMode, propertyId, form.checkIn, form.checkOut, onChange]);

  /* ===== nopti ===== */
  const nights = useMemo(() => {
    if (!form.checkIn || !form.checkOut) return 1;
    return Math.max(1, Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / 86400000));
  }, [form.checkIn, form.checkOut]);

  /* ===== pret ===== */
  const totalPrice = useMemo(() => {
    let total = 0;

    // camere pentru adulti
    ROOM_TYPES.forEach(room => {
      const qty = form.rooms?.[room] || 0;
      const price = form.roomsPrice?.[room] || 0;
      total += qty * price * nights * (form.adults || 1);
    });

    // copii
    (form.childrenAges || []).forEach(age => {
      const rule = getChildRule(age);
      ROOM_TYPES.forEach(room => {
        const qty = form.rooms?.[room] || 0;
        const price = form.roomsPrice?.[room] || 0;
        total += qty * price * nights * ((100 - rule.childDiscount) / 100);
      });
      if (form.selectedMeal) total += rule.mealPrice * nights;
    });

    // optiuni masa doar daca guest a selectat
    if (guestMode && form.selectedMeal) {
      total += (form.mealOptions?.[form.selectedMeal] || 0) * nights * (form.adults || 1);
    }

    // optiuni masa pentru host (edit)
    if (!guestMode) {
      Object.entries(form.mealOptions || {}).forEach(([key, value]) => {
        if (typeof value === 'number' && value > 0) total += value * nights;
      });
    }

    return total;
  }, [form, nights, guestMode]);

  /* ===== calcul discount ===== */
  const discountAmount = useMemo(() => {
    if (!form.discount || form.discount <= 0) return 0;
    return parseFloat((totalPrice * (form.discount / 100)).toFixed(2));
  }, [form.discount, totalPrice]);

  /* ===== total final ===== */
  const finalPrice = useMemo(() => {
    return parseFloat((totalPrice - discountAmount).toFixed(2));
  }, [totalPrice, discountAmount]);

  useEffect(() => {
    ROOM_TYPES.forEach(room => {
      const reserved = form.rooms?.[room] || 0;
      const available = form.roomsAvailable?.[room] || 0;

      if (reserved > available) {
        onChange({
          target: {
            name: 'rooms',
            value: { ...form.rooms, [room]: available }
          }
        });
      }
    });
  }, [form.roomsAvailable]);

  /* ===== UI helpers ===== */
  const wrap = {
    fontFamily: 'Sora, sans-serif',
    color: '#000',
    fontSize: 13,
    padding: '0.5rem'
  };

  const card = {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 8,
    marginBottom: 8
  };

  const title = {
    fontSize: 13,
    fontWeight: 600,
    color: '#006d77',
    marginBottom: 6
  };

  const Row = ({ label, children }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 6
    }}>
      <span>{label}</span>
      {children}
    </div>
  );

  const inputStyle = {
    flex: 1,
    padding: 6,
    fontSize: 12,
    border: '1px solid #ccc',
    borderRadius: 6
  };

  // TOȚI utilizatorii văd amenitățile din form.amenities
  const displayAmenities = useMemo(() => {
    if (!form.amenities) return [];

    if (Array.isArray(form.amenities)) {
      return form.amenities;
    }

    if (typeof form.amenities === 'string') {
      try {
        const parsed = JSON.parse(form.amenities);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  }, [form.amenities]);

  return (
    <div style={{ fontFamily: 'Sora, sans-serif', color: '#006d77' }}>
      {/* DOTĂRI GENERALE */}
      <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Dotări generale</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.3rem' }}>
        {GENERAL_AMENITIES.map(key => {
          const generalAmenitiesArray = Array.isArray(amenities.generalAmenities)
            ? amenities.generalAmenities
            : JSON.parse(amenities.generalAmenities || '[]');

          return canEditAmenities ? (
            <label key={key} style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', fontSize: '0.7rem' }}>
              <input
                type="checkbox"
                name="generalAmenities"           // <--- important
                value={key}                       // <--- important
                checked={generalAmenitiesArray.includes(key)}
                onChange={onChange}               // folosește handleChange din Property.jsx
                style={{ accentColor: '#006d77' }}
              />
              {key}
            </label>
          ) : (
            generalAmenitiesArray.includes(key) && (
              <div key={key} style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', fontSize: '0.7rem' }}>
                <span style={{ color: '#dd4124', fontWeight: 700 }}>✔</span>
                <span>{key}</span>
              </div>
            )
          );
        })}
      </div>

      {/* AGROTURISM */}
      <h3 style={{ marginTop: '1rem', fontSize: '1rem', marginBottom: '0.5rem' }}>Agroturism</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.3rem' }}>
        {AGROTURISM_AMENITIES.map(key => {
          const agroAmenitiesArray = Array.isArray(amenities.agroAmenities)
            ? amenities.agroAmenities
            : JSON.parse(amenities.agroAmenities || '[]');

          return canEditAmenities ? (
            <label key={key} style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', fontSize: '0.7rem' }}>
              <input
                type="checkbox"
                name="agroAmenities"              // <--- important
                value={key}                        // <--- important
                checked={agroAmenitiesArray.includes(key)}
                onChange={onChange}                // folosește handleChange din Property.jsx
                style={{ accentColor: '#006d77' }}
              />
              {key}
            </label>
          ) : (
            agroAmenitiesArray.includes(key) && (
              <div key={key} style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', fontSize: '0.7rem' }}>
                <span style={{ color: '#dd4124', fontWeight: 700 }}>✔</span>
                <span>{key}</span>
              </div>
            )
          );
        })}
      </div>

      {/* ZONA REZERVARE – DOAR GUEST */}
      {guestMode && (
        <>
          {/* ================== DATE SEJUR ================== */}
          <section style={card}>
            <h4 style={title}>Perioada</h4>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="date"
                value={form.checkIn || ''}
                onChange={onChange}
                name="checkIn"
                style={inputStyle}
              />
              <input
                type="date"
                value={form.checkOut || ''}
                onChange={onChange}
                name="checkOut"
                style={inputStyle}
              />
            </div>
          </section>

          {/* ================== PERSOANE ================== */}
          <section style={card}>
            <h4 style={title}>Oaspeti</h4>

            <Row label="Adulti">
              <Counter
                value={form.adults || 1}
                min={1}
                onChange={v => onChange({ target: { name: 'adults', value: v } })}
              />
            </Row>

            <Row label="Copii">
              <Counter
                value={form.children || 0}
                min={0}
                onChange={v => {
                  onChange({ target: { name: 'children', value: v } });
                  const ages = [...(form.childrenAges || [])];
                  while (ages.length < v) ages.push(3);
                  while (ages.length > v) ages.pop();
                  onChange({ target: { name: 'childrenAges', value: ages } });
                }}
              />
            </Row>
          </section>
        </>
      )}

      {/* ================== CAMERE SI PRETURI ================== */}
      <section style={card}>
        <h4 style={title}>Camere si preturi</h4>

        {ROOM_TYPES.map(room => (
          <div key={room} style={{
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: 6,
            marginBottom: 6
          }}>
            <div style={{ fontWeight: 500, color: '#006d77', marginBottom: 4 }}>{room}</div>

            {/* Pret camera */}
            <Row label="Pret/noapte:">
              {canEditAmenities ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    value={form.roomsPrice?.[room] || 0}
                    onChange={e => onChange({
                      target: {
                        name: 'roomsPrice',
                        value: { ...form.roomsPrice, [room]: Math.max(0, Number(e.target.value)) }
                      }
                    })}
                    min="0"
                    style={{ width: 60, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                    disabled={!isHost || !editMode}
                  />
                  <span style={{ fontSize: 11, color: '#666' }}>RON</span>
                </div>
              ) : (
                <span style={{ color: '#dd4124', fontWeight: 500, fontSize: 12 }}>
                  {form.roomsPrice?.[room] ? `${form.roomsPrice[room]} RON` : '—'}
                </span>
              )}
            </Row>
            {/* Counter disponibilitate / selectie */}
            <Row label={isHost ? "Total camere disponibile:" : "Camere de rezervat:"}>
              <Counter
                value={isHost ? form.roomsAvailable?.[room] || 0 : form.rooms?.[room] || 0}
                min={0}
                max={isHost ? 1000 : form.roomsAvailable?.[room] || 0} // Host poate pune orice, guest maxim cat e disponibil
                onChange={v => {
                  if (isHost) {
                    // Actualizeaza cate camere sunt disponibile
                    onChange({
                      target: {
                        name: 'roomsAvailable',
                        value: { ...form.roomsAvailable, [room]: v }
                      }
                    });
                  } else {
                    // Guest selecteaza cate camere vrea sa rezerve
                    onChange({
                      target: {
                        name: 'rooms',
                        value: { ...form.rooms, [room]: v }
                      }
                    });
                  }
                }}
                disabled={isHost ? !editMode : !guestMode} // host doar in editMode, guest doar daca e guestMode
              />
            </Row>
          </div>
        ))}
      </section>

      {/* ================== OPTIUNI MASA ================== */}
      <section style={card}>
        <h4 style={title}>Optiuni masa</h4>

        {/* mic dejun inclus */}
        <Row label="Mic dejun inclus">
          <input
            type="checkbox"
            checked={!!form.mealOptions?.breakfastIncluded}
            onChange={e => onChange({
              target: {
                name: 'mealOptions',
                value: { ...form.mealOptions, breakfastIncluded: e.target.checked }
              }
            })}
            disabled={!isHost || !editMode}
            style={{ accentColor: '#006d77', width: 16, height: 16 }}
          />
        </Row>

        {/* optiuni cu pret */}
        {['breakfast', 'demiPension', 'allInclusive'].map(key => {
          const label = { breakfast: 'Mic dejun', demiPension: 'Demi-pensiune', allInclusive: 'All Inclusive' }[key];
          const price = form.mealOptions?.[key] ?? 0;
          const checked = canEditAmenities ? price > 0 : form.selectedMeal === key;

          return (
            <Row key={key} label={label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={canEditAmenities ? false : price === 0}
                  onChange={() => {
                    if (canEditAmenities) {
                      onChange({ target: { name: 'mealOptions', value: { ...form.mealOptions, [key]: price > 0 ? undefined : 0 } } });
                    } else {
                      onChange({ target: { name: 'selectedMeal', value: form.selectedMeal === key ? '' : key } });
                    }
                  }}
                  style={{ accentColor: '#006d77', width: 16, height: 16 }}
                />
                {canEditAmenities ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      value={price}
                      onChange={e => onChange({ target: { name: 'mealOptions', value: { ...form.mealOptions, [key]: Number(e.target.value) } } })}
                      min="0"
                      style={{ width: 50, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                      disabled={!isHost || !editMode}
                    />
                    <span style={{ fontSize: 11, color: '#666' }}>RON</span>
                  </div>
                ) : (
                  price > 0 && <span style={{ color: '#dd4124', fontWeight: 500, fontSize: 12 }}>{price} RON</span>
                )}
              </div>
            </Row>
          );
        })}
      </section>

      {/* ================== REGULI COPII ================== */}
      <section style={card}>
        <h4 style={title}>Reguli pentru copii</h4>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(form.childRules || CHILD_RULES).map((rule, index) => (
            <div key={index} style={{
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              padding: 6,
              fontSize: 12
            }}>
              <div style={{ fontWeight: 500, marginBottom: 4, color: '#006d77' }}>
                Copii {rule.min}-{rule.max} ani
              </div>

              <Row label="Reducere camera:">
                {canEditAmenities ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      value={rule.childDiscount}
                      onChange={(e) => {
                        const newRules = [...(form.childRules || CHILD_RULES)];
                        if (newRules[index]) {
                          newRules[index].childDiscount = Math.max(0, Math.min(100, Number(e.target.value)));
                          onChange({ target: { name: 'childRules', value: newRules } });
                        }
                      }}
                      min="0"
                      max="100"
                      style={{ width: 45, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                      disabled={!isHost || !editMode}
                    />
                    <span style={{ fontSize: 11, color: '#666' }}>%</span>
                  </div>
                ) : (
                  <span style={{ fontWeight: 500, fontSize: 12 }}>
                    {rule.childDiscount === 100 ? '100% (gratis)' :
                      rule.childDiscount === 0 ? '0% (pret intreg)' :
                        `${rule.childDiscount}% reducere`}
                  </span>
                )}
              </Row>

              <Row label="Masa (RON/noapte):">
                {canEditAmenities ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="number"
                      value={rule.mealPrice}
                      onChange={(e) => {
                        const newRules = [...(form.childRules || CHILD_RULES)];
                        if (newRules[index]) {
                          newRules[index].mealPrice = Math.max(0, Number(e.target.value));
                          onChange({ target: { name: 'childRules', value: newRules } });
                        }
                      }}
                      min="0"
                      style={{ width: 45, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                      disabled={!isHost || !editMode}
                    />
                    <span style={{ fontSize: 11, color: '#666' }}>RON</span>
                  </div>
                ) : (
                  <span style={{ fontWeight: 500, fontSize: 12 }}>{rule.mealPrice} RON</span>
                )}
              </Row>

              <div style={{ fontSize: 11, color: '#666', marginTop: 4, fontStyle: 'italic' }}>
                {rule.note}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================== DISCOUNT ================== */}
      {canEditAmenities && (
        <section style={card}>
          <h4 style={title}>Discount general</h4>
          <Row label="Discount pentru rezervare:">
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number"
                value={form.discount || 0}
                onChange={e => onChange({ target: { name: 'discount', value: Math.max(0, Math.min(100, Number(e.target.value))) } })}
                min="0"
                max="100"
                style={{ width: 45, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                disabled={!isHost || !editMode}
              />
              <span style={{ fontSize: 11, color: '#666' }}>%</span>
            </div>
          </Row>
        </section>
      )}

      {/* ================== VARSTE COPII ================== */}
      {(form.childrenAges || []).map((age, i) => {
        const rule = getChildRule(age);
        return (
          <section key={i} style={card}>
            <h4 style={title}>Copil {i + 1}</h4>
            <Row label="Varsta:">
              <input
                type="number"
                min={0} max={17}
                value={age}
                onChange={e => {
                  const ages = [...form.childrenAges];
                  ages[i] = Number(e.target.value);
                  onChange({ target: { name: 'childrenAges', value: ages } });
                }}
                style={{ width: 50, padding: '2px 4px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                disabled={!isHost || !editMode}
              />
            </Row>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              {rule.childDiscount === 100 ? '✓ Camera gratis' :
                rule.childDiscount === 0 ? '✓ Pret intreg camera' :
                  `✓ ${rule.childDiscount}% reducere camera`} • {rule.mealPrice} RON/noapte pentru masa
            </div>
          </section>
        );
      })}

      {/* ================== TIP REZERVARE ================== */}
      <section style={card}>
        <h4 style={title}>Alege tip rezervare</h4>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
          {[
            {
              key: 'freeCancel',
              label: (
                <span>
                  Anulare gratuita pana la{' '}
                  {isHost && editMode ? (
                    <input
                      type="number"
                      name="freeCancellationDays"
                      min={1}
                      max={30}
                      value={form.freeCancellationDays || 2}
                      onChange={onChange}
                      style={{ width: 35, margin: '0 4px', fontSize: 11, padding: '2px 4px' }}
                      disabled={!isHost || !editMode}
                    />
                  ) : (
                    <strong>{property?.freeCancellationDays || 2}</strong>
                  )}
                  {' '}zile inainte de check-in
                </span>
              ),
              description: 'Nu platesti nimic acum. Vei primi notificari de confirmare.',
              note: 'Cel mai flexibil'
            },
            {
              key: 'oneNight',
              label: `Rezerva cu achitarea unei nopti (nerambursabil)`,
              description: 'Plata unei nopti protejeaza gazda.',
              note: 'Bun pentru planificari'
            },
            {
              key: 'full',
              label: `Rezerva cu plata integrala`,
              description: 'Plata se face pentru toate noptile rezervate.',
              note: 'Rezervare garantata'
            }
          ].map(option => (
            <label
              key={option.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 6,
                padding: 6,
                cursor: 'pointer',
                backgroundColor: form.bookingType === option.key ? '#F0F9FF' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <input
                  type="radio"
                  name="bookingType"
                  value={option.key}
                  checked={form.bookingType === option.key}
                  onChange={onChange}
                  style={{
                    accentColor: '#006d77',
                    marginTop: 2,
                    width: 14,
                    height: 14
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#006d77',
                    marginBottom: 2
                  }}>
                    {option.label}
                  </div>
                  <div style={{
                    fontSize: 11,
                    color: '#666',
                    marginBottom: 2
                  }}>
                    {option.description}
                  </div>
                  {option.note && (
                    <div style={{
                      fontSize: 10,
                      color: '#888',
                      fontStyle: 'italic'
                    }}>
                      {option.note}
                    </div>
                  )}
                </div>
              </div>

              {option.key === 'freeCancel' && property?.freeCancellationDays && !isHost && (
                <div style={{
                  fontSize: 10,
                  color: '#006d77',
                  marginTop: 2,
                  padding: 4,
                  backgroundColor: '#E0F7FA',
                  borderRadius: 4
                }}>
                  ⏱️ Anulare gratuita pana la <strong>{property.freeCancellationDays} zile</strong> inainte de check-in
                </div>
              )}
            </label>
          ))}
        </div>
      </section>

      {/* ================== TOTAL ================== */}
      <section style={{ ...card, background: '#f7f7f7' }}>
        <Row label="Total inainte de discount:">
          <strong>{totalPrice.toFixed(2)} RON</strong>
        </Row>

        {form.discount > 0 && (
          <>
            <Row label={`Discount (${form.discount}%):`}>
              <strong style={{ color: '#dd4124' }}>-{discountAmount.toFixed(2)} RON</strong>
            </Row>
            <div style={{
              borderTop: '1px dashed #ddd',
              marginTop: 6,
              paddingTop: 6,
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 600,
              fontSize: 13,
              color: '#006d77'
            }}>
              <span>Total final:</span>
              <span>{finalPrice.toFixed(2)} RON</span>
            </div>
          </>
        )}

        {form.discount <= 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 600,
            fontSize: 13,
            color: '#006d77',
            marginTop: 6
          }}>
            <span>Total final:</span>
            <span>{finalPrice.toFixed(2)} RON</span>
          </div>
        )}
      </section>
    </div >
  );
}